import React, { useState, useEffect } from 'react';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextField, Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { LiveFilterComponentProps } from '../../types/types';
import { TranslationEnum, usePortalTranslation } from '../../../../providers/TranslationProvider';
import './LiveFilterComponent.css';

const LiveFilterComponent: React.FC<LiveFilterComponentProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  selectedFilter,
  setSelectedFilter,
  minDate,
  selectedParentFilter
}) => {
  const { translate } = usePortalTranslation();
  const [dateRangeType, setDateRangeType] = useState<'daterange' | 'period'>('daterange');

  // Filter options
  const filterOptions = [
    { id: 'past7days', label: 'Past 7 Days', type: 'days', value: 7 },
    // { id: 'past1month', label: 'Past 1 Month', type: 'months', value: 1 },
    { id: 'past3months', label: 'Past 3 Months', type: 'months', value: 3 },
    { id: 'custom', label: 'Custom Range', type: null, value: null }
  ];

  // Set default to 'past7days' filter on component mount
  useEffect(() => {
    if (!selectedFilter) {
      setSelectedFilter('past7days');
      const end = dayjs();
      const start = end.subtract(7, 'day');
      setStartDate(start);
      setEndDate(end);
    }
  }, []);

  // Also ensure dates are set correctly when selectedFilter changes to 'past7days'
  useEffect(() => {
    if (selectedFilter === 'past7days') {
      const end = dayjs();
      const start = end.subtract(7, 'day'); 
      console.log('Live filter selected, setting dates:', { start: start.format('DD-MM-YYYY'), end: end.format('DD-MM-YYYY') });
      setStartDate(start);
      setEndDate(end);
    }
  }, [selectedFilter]);

  const handleFilterSelect = (filterId: string) => {
    setSelectedFilter(filterId);
    
    const filter = filterOptions.find(opt => opt.id === filterId);
    if (filter && filter.type && filter.value) {
      let end = dayjs().subtract(1, 'day'); // Default to yesterday
      let start = end; // Default start date
      
      if (filter.type === 'days') {
        // For Live filter, use today as To date
        end = dayjs(); // To date is today
        start = end.subtract(filter.value, 'day'); // From date is 7 days before today
      } else if (filter.type === 'months') {
        // For Past 1 Month and Past 3 Months, use yesterday as To date
        end = dayjs().subtract(1, 'day'); // To date is yesterday
        start = end.subtract(filter.value, 'month'); // From date is X months before yesterday
      }
      
      setStartDate(start!);
      setEndDate(end!);
    } else if (filterId === 'custom') {
      const yesterday = dayjs().subtract(1, 'day');
      setStartDate(yesterday);
      setEndDate(yesterday);
    }
  };

  // Calculate date constraints for different filters
  const getDateConstraints = () => {
    if (selectedFilter === 'past7days') {
      const today = dayjs();
      const sevenDaysAgo = today.subtract(7, 'day'); // 7 days total including today
      
      return {
        maxDate: today, // To date can be today
        minDate: sevenDaysAgo, 
        maxStartDate: today, 
        minEndDate: sevenDaysAgo, 
        maxRangeDays: 7 // Maximum 7 days range
      };
    // } else if (selectedFilter === 'past1month') {
    //   const yesterday = dayjs().subtract(1, 'day');
    //   const oneMonthAgo = yesterday.subtract(1, 'month'); // 1 month ago from yesterday
      
    //   return {
    //     maxDate: yesterday, 
    //     minDate: oneMonthAgo,
    //     maxStartDate: yesterday, 
    //     minEndDate: oneMonthAgo, 
    //     maxRangeDays: 31 // Maximum 31 days range (1 month)
    //   };
    } else if (selectedFilter === 'past3months') {
      const yesterday = dayjs().subtract(1, 'day');
      const threeMonthsAgo = yesterday.subtract(3, 'month'); // 3 months ago from yesterday
      
      return {
        maxDate: yesterday, 
        minDate: threeMonthsAgo, 
        maxStartDate: yesterday, 
        minEndDate: threeMonthsAgo, 
        maxRangeDays: 93 // Maximum 93 days range (3 months)
      };
    }
    
    // For custom range, use default constraints
    return {
      maxDate: dayjs(),
      minDate: minDate,
      maxStartDate: dayjs(),
      minEndDate: minDate,
      maxRangeDays: null // No range limit for custom
    };
  };

  const dateConstraints = getDateConstraints();

  // const handleDateRangeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setDateRangeType(event.target.value as 'daterange' | 'period');
  // };

  const formatDateRange = (filterId: string) => {
    const filter = filterOptions.find(opt => opt.id === filterId);
    if (!filter || !filter.type || !filter.value) return filter?.label || '';
    
    const end = dayjs();
    let start;
    
    if (filter.type === 'days') {
      start = end.subtract(filter.value, 'day');
    } else if (filter.type === 'months') {
      start = end.subtract(filter.value, 'month');
    }
    
    return `${start?.format('DD-MM-YYYY')} to ${end.format('DD-MM-YYYY')}`;
  };

  const getDisclaimerText = () => {
    if (!selectedFilter) return '';
    
    switch (selectedFilter) {
      case 'past7days':
        return 'Note: Data for last 7 days is readily available to download.';
      case 'past3months':
        return 'Note: Last 3 months data is approximately 1 hour old.';
      case 'custom':
        return 'Note: Data from custom date range is approximately 1 hour old.';
      default:
        return '';
    }
  };

  return (
    <div className="live-filter-component">
      {/* Filter Options */}
      <div className="live-filter-options">
        {filterOptions.map((option) => (
          <div
            key={option.id}
            className={`live-filter-option ${selectedFilter === option.id ? 'selected' : ''}`}
            onClick={() => handleFilterSelect(option.id)}
          >
            <div className="filter-label">{option.label}</div>
            {/* {option.days && (
              <div className="filter-date-range">
                {formatDateRange(option.id)}
              </div>
            )} */}
          </div>
        ))}
      </div>

      {/* Date Range Type Selection */}
      {/* <div className="date-range-type-selection">
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={dateRangeType}
            onChange={handleDateRangeTypeChange}
            className="date-range-radio-group"
          >
            <FormControlLabel
              value="daterange"
              control={<Radio />}
              label={translate(TranslationEnum.common_portal, "Date range")}
            />
            <FormControlLabel
              value="period"
              control={<Radio />}
              label={translate(TranslationEnum.common_portal, "Period filter")}
            />
          </RadioGroup>
        </FormControl>
      </div> */}

      {/* Date Input Fields */}
      {dateRangeType === 'daterange' && (
        <div className="live-filter-date-inputs">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={translate(TranslationEnum.common_portal, "From date")}
              inputFormat="DD-MM-YYYY"
              value={startDate}
              maxDate={dateConstraints.maxStartDate}
              minDate={dateConstraints.minDate}
              onChange={(value) => {
                if (value) {
                  setStartDate(value);
                  // Adjust end date to maintain range constraint based on selected filter
                  if (selectedFilter === 'past7days' || selectedFilter === 'past1month' || selectedFilter === 'past3months') {
                    const maxRangeDays = dateConstraints.maxRangeDays;
                    if (maxRangeDays) {
                      const maxAllowedDate = selectedFilter === 'past7days' ? dayjs() : dayjs().subtract(1, 'day');
                      const maxEndDate = value.add(maxRangeDays - 1, 'day'); // Subtract 1 because we include both start and end dates
                      const newEndDate = maxEndDate.isAfter(maxAllowedDate) ? maxAllowedDate : maxEndDate;
                      setEndDate(newEndDate);
                    }
                  }
                }
              }}
              renderInput={(props) => (
                <TextField {...props} size="small" className="date-input" />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={translate(TranslationEnum.common_portal, "To date")}
              inputFormat="DD-MM-YYYY"
              value={endDate}
              maxDate={dateConstraints.maxDate}
              minDate={dateConstraints.minEndDate}
              onChange={(value) => {
                if (value) {
                  setEndDate(value);
                  // Adjust start date to maintain range constraint based on selected filter
                  if (selectedFilter === 'past7days' || selectedFilter === 'past1month' || selectedFilter === 'past3months') {
                    const maxRangeDays = dateConstraints.maxRangeDays;
                    if (maxRangeDays) {
                      const minStartDate = value.subtract(maxRangeDays - 1, 'day'); // Subtract 1 because we include both start and end dates
                      const absoluteMinDate = dateConstraints.minDate;
                      const newStartDate = minStartDate.isBefore(absoluteMinDate) ? absoluteMinDate : minStartDate;
                      setStartDate(newStartDate);
                    }
                  }
                }
              }}
              renderInput={(props) => (
                <TextField {...props} size="small" className="date-input" />
              )}
            />
          </LocalizationProvider>
        </div>
      )}
      
      {/* Disclaimer */}
      {selectedFilter && (
        <div className="daterange-disclaimer" style={{ marginTop: '10px', fontSize: '12px' , fontWeight: '500'}}>
          {getDisclaimerText()}
        </div>
      )}
    </div>
  );
};

export default LiveFilterComponent;
